import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { Organization } from '../../types/organization';
import { useOrganizations } from '../../hooks/useOrganizations';
import toast from 'react-hot-toast';
import { BuildingOfficeIcon, MapPinIcon, UserCircleIcon, CloudArrowUpIcon, XMarkIcon, BoltIcon, CheckCircleIcon, XCircleIcon as XCircleIconOutline, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useStorage } from '../../hooks/useStorage';
import OrganizationUsers from './OrganizationUsers';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatTaxNumber, formatRegistrationNumber } from '../../utils/validation';
import { getBillingSettings, saveBillingSettings, validateAgentKey } from '../../services/billingService';
import { BillingSettings as BillingSettingsType } from '../../types/billing';
import { syncEmailTestModeFromOrg } from '../../services/emailService';

interface FormData {
  name: string;
  taxNumber: string;
  registrationNumber: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  contact: {
    name: string;
    email: string;
    phone: string;
    position: string;
  };
}

const initialFormData: FormData = {
  name: '',
  taxNumber: '',
  registrationNumber: '',
  address: {
    street: '',
    city: '',
    postalCode: '',
    country: ''
  },
  contact: {
    name: '',
    email: '',
    phone: '',
    position: ''
  }
};

const FORM_SECTIONS = {
  company: {
    title: 'Company Information',
    icon: BuildingOfficeIcon,
    color: 'from-blue-500 to-indigo-600',
    fields: [
      { name: 'name', label: 'Company Name', required: true },
      { name: 'taxNumber', label: 'Tax Number', required: true },
      { name: 'registrationNumber', label: 'Registration Number', required: true }
    ]
  },
  address: {
    title: 'Registered Address',
    icon: MapPinIcon,
    color: 'from-emerald-500 to-teal-600',
    fields: [
      { name: 'address.street', label: 'Street', required: true },
      { name: 'address.city', label: 'City', required: true },
      { name: 'address.postalCode', label: 'Postal Code', required: true },
      { name: 'address.country', label: 'Country', required: true }
    ]
  },
  contact: {
    title: 'Contact Person',
    icon: UserCircleIcon,
    color: 'from-purple-500 to-pink-600',
    fields: [
      { name: 'contact.name', label: 'Name', required: true },
      { name: 'contact.email', label: 'Email', required: true },
      { name: 'contact.phone', label: 'Phone', required: true },
      { name: 'contact.position', label: 'Position', required: false }
    ]
  }
};

interface OrganizationSettingsProps {
  organization: Organization;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
  onOrganizationUpdate: (org: Organization) => void;
}

export const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({
  organization,
  isEditing,
  onEditingChange,
  onOrganizationUpdate
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const { uploadFile, deleteFile } = useStorage();
  const [isUploading, setIsUploading] = useState(false);
  const { t } = useLanguage();
  
  // Billing settings state
  const [billingSettings, setBillingSettings] = useState<BillingSettingsType | null>(null);
  const [agentKey, setAgentKey] = useState('');
  const [showAgentKey, setShowAgentKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isValidKey, setIsValidKey] = useState<boolean | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Bank account data
  const [bankHuf, setBankHuf] = useState('OTP Bank Nyrt.');
  const [bankAccountHuf, setBankAccountHuf] = useState('11742173-26044282');
  const [bankEur, setBankEur] = useState('Slovenská sporiteľňa, a. s.');
  const [ibanEur, setIbanEur] = useState('SK23 0900 0000 0050 7704 3706');
  const [swiftEur, setSwiftEur] = useState('GIBASKBX');

  useEffect(() => {
    setFormData({
      name: organization.name,
      taxNumber: organization.taxNumber,
      registrationNumber: organization.registrationNumber,
      address: organization.address,
      contact: organization.contact
    });
    
    // Load billing settings for this organization
    loadBillingSettings();
  }, [organization]);

  const loadBillingSettings = async () => {
    try {
      const settings = await getBillingSettings(organization.id);
      if (settings) {
        setBillingSettings(settings);
        setAgentKey(settings.agentKey);
        setIsValidKey(true);
        
        // Load bank account data if exists
        if (settings.sellerData) {
          if (settings.sellerData.bank) setBankHuf(settings.sellerData.bank);
          if (settings.sellerData.bankAccount) setBankAccountHuf(settings.sellerData.bankAccount);
          if (settings.sellerData.bankEur) setBankEur(settings.sellerData.bankEur);
          if (settings.sellerData.ibanEur) setIbanEur(settings.sellerData.ibanEur);
          if (settings.sellerData.swiftEur) setSwiftEur(settings.sellerData.swiftEur);
        }
        
        // Sync email test mode from org settings to localStorage
        syncEmailTestModeFromOrg(settings.emailTestMode, settings.emailTestAddress);
      }
    } catch (error) {
      console.error('Error loading billing settings:', error);
    }
  };

  const handleValidateAgentKey = async () => {
    if (!agentKey.trim()) {
      toast.error('Add meg az agent key-t');
      return;
    }

    try {
      setIsValidating(true);
      const result = await validateAgentKey(agentKey);
      
      if (result.valid) {
        setIsValidKey(true);
        toast.success('Agent key érvényes!');
      } else {
        setIsValidKey(false);
        toast.error('Agent key érvénytelen');
      }
    } catch (error: any) {
      setIsValidKey(false);
      toast.error(`Validálási hiba: ${error.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSaveBillingSettings = async () => {
    if (!agentKey.trim()) {
      toast.error('Add meg az agent key-t');
      return;
    }

    if (isValidKey === false) {
      toast.error('Először validáld az agent key-t');
      return;
    }

    try {
      const settingsData: Omit<BillingSettingsType, 'id' | 'createdAt' | 'updatedAt'> = {
        organizationId: organization.id,
        agentKey: agentKey.trim(),
        isActive: true,
        sellerData: {
          ...billingSettings?.sellerData,
          bank: bankHuf,
          bankAccount: bankAccountHuf,
          bankEur: bankEur,
          ibanEur: ibanEur,
          swiftEur: swiftEur
        },
        emailTestMode: billingSettings?.emailTestMode || false,
        emailTestAddress: billingSettings?.emailTestAddress || 'nroland89@gmail.com'
      };

      await saveBillingSettings(settingsData);
      
      toast.success('Számlázási beállítások mentve!');
      setHasChanges(false);
      await loadBillingSettings();
    } catch (error: any) {
      console.error('Error saving billing settings:', error);
      toast.error(`Mentési hiba: ${error.message}`);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Apply formatting for tax number and registration number
    if (name === 'taxNumber') {
      const formattedValue = formatTaxNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      return;
    } else if (name === 'registrationNumber') {
      const formattedValue = formatRegistrationNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      return;
    }
    
    // Handle other fields normally
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...(prev[section as keyof FormData] as Record<string, string>),
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await organizationService.updateOrganization(organization.id, formData);

      const updatedOrgData: Organization = {
        ...organization,
        ...formData,
        updatedAt: new Date()
      };

      onOrganizationUpdate(updatedOrgData);

      toast.success(typeof t('common.messages.organization.updateSuccess') === 'string' 
        ? t('common.messages.organization.updateSuccess') 
        : 'Szervezet sikeresen frissítve');
      onEditingChange(false);
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error(typeof t('common.messages.organization.updateError') === 'string'
        ? t('common.messages.organization.updateError')
        : 'Hiba történt a szervezet frissítésekor');
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const path = `organizations/${organization.id}/logo`;
      const logoUrl = await uploadFile(file, path);

      const updatedOrg = {
        ...organization,
        logoUrl
      } as Organization;

      await organizationService.updateOrganization(organization.id, updatedOrg);
      onOrganizationUpdate(updatedOrg);

      toast.success(typeof t('common.messages.organization.logoUploadSuccess') === 'string'
        ? t('common.messages.organization.logoUploadSuccess')
        : 'Logó sikeresen feltöltve');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(typeof t('common.messages.organization.logoUploadError') === 'string'
        ? t('common.messages.organization.logoUploadError')
        : 'Hiba történt a logó feltöltésekor');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!organization?.logoUrl) return;

    try {
      const path = `organizations/${organization.id}/logo`;
      await deleteFile(path);

      const updatedOrg = {
        ...organization,
        logoUrl: null
      } as Organization;

      await organizationService.updateOrganization(organization.id, updatedOrg);
      onOrganizationUpdate(updatedOrg);

      toast.success('Logo removed successfully');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    }
  };

  const renderField = (label: string, name: string, required: boolean = true) => {
    const getValue = (path: string) => {
      const keys = path.split('.');
      let value: any = formData;
      for (const key of keys) {
        if (value === undefined) return '';
        value = value[key];
      }
      return value || '';
    };

    // Add placeholders for tax number and registration number fields
    let placeholder = '';
    if (name === 'taxNumber') {
      placeholder = '12345678-1-23';
    } else if (name === 'registrationNumber') {
      placeholder = '01-10-123456';
    }

    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {typeof label === 'string' ? label : name} {required && <span className="text-red-500">*</span>}
        </label>
        {isEditing ? (
          <input
            type="text"
            name={name}
            value={getValue(name)}
            onChange={handleChange}
            required={required}
            placeholder={placeholder}
            className="block w-full bg-white border border-gray-300 rounded-lg py-2 px-2 sm:py-2.5 sm:px-4 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 text-sm sm:text-base overflow-hidden text-ellipsis shadow-sm"
          />
        ) : (
          <p className="py-2.5 px-4 bg-gray-100 rounded-lg text-gray-700 overflow-hidden text-ellipsis">{getValue(name) || '—'}</p>
        )}
      </div>
    );
  };

  const getTranslation = (key: string, fallback: string): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : fallback;
  };

  return (
    <div className="space-y-8 mb-4">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 sm:gap-0">
        <h2 className="text-2xl font-bold text-gray-800">
          {getTranslation('organizations.fields.organizationDetails', 'Szervezeti adatok')}
        </h2>
        {!isEditing && (user?.role === 'admin' || user?.role === 'super_admin') && (
          <button
            onClick={() => onEditingChange(true)}
            className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-2.5 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium shadow-lg shadow-primary-500/25"
          >
            {getTranslation('organizations.buttons.editDetails', 'Adatok szerkesztése')}
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {Object.entries(FORM_SECTIONS).map(([sectionKey, section]) => (
          <div
            key={sectionKey}
            className="bg-white rounded-xl p-3 sm:p-6 border border-gray-200 shadow-md"
          >
            <div className="flex items-center space-x-3 mb-4 sm:mb-6">
              <div className={`p-2 sm:p-2.5 rounded-lg bg-gradient-to-br ${section.color}`}>
                <section.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                {getTranslation(`organizations.fields.${sectionKey}.title`, section.title)}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {section.fields.map((field) => (
                <React.Fragment key={field.name}>
                  {renderField(
                    getTranslation(`organizations.fields.${sectionKey}.${field.name}`, field.label),
                    field.name,
                    field.required
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}

        {isEditing && (
          <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={() => {
                onEditingChange(false);
              }}
              className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 font-medium"
            >
              {getTranslation('organizations.fields.cancelChanges', 'Változtatások elvetése')}
            </button>
            <button
              type="submit"
              className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-2.5 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium shadow-lg shadow-primary-500/25"
            >
              {getTranslation('organizations.fields.saveChanges', 'Változtatások mentése')}
            </button>
          </div>
        )}
      </form>

      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">
          {getTranslation('organizations.fields.logo.organizationLogo', 'Szervezet logója')}
        </h3>
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-32 h-32 rounded-lg border-2 border-dashed border-gray-300 overflow-hidden">
            {organization?.logoUrl ? (
              <div className="relative group">
                <img
                  src={organization.logoUrl}
                  alt="Organization logo"
                  className="w-full h-full object-contain"
                />
                <button
                  onClick={handleLogoDelete}
                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 
                           flex items-center justify-center text-white transition-opacity"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                {getTranslation('organizations.fields.logo.noLogo', 'Nincs logó')}
              </div>
            )}
          </div>

          <div className="flex-1 mt-2 sm:mt-0">
            <label className="block">
              <span className="sr-only">
                {getTranslation('organizations.fields.logo.chooseLogoFile', 'Logófájl kiválasztása')}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={isUploading}
                className="block w-full text-sm text-gray-500
                         file:mr-4 file:py-2 file:px-4
                         file:rounded-md file:border-0
                         file:text-sm file:font-medium
                         file:bg-primary-600 file:text-white
                         file:cursor-pointer file:hover:bg-primary-700
                         disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </label>
            <p className="mt-2 text-sm text-gray-500">
              {getTranslation('organizations.fields.logo.recommendedSize', 'Ajánlott méret: 200x200 pixel')}
            </p>
          </div>
        </div>
      </div>

      {/* Számlázz.hu Integration Settings */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2.5 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600">
            <BoltIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Számlázz.hu Integráció</h3>
            <p className="text-sm text-gray-500">Állítsd be a Számlázz.hu agent key-t az automatikus számlázáshoz</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Számlázz.hu Agent Key *
            </label>
            <div className="relative">
              <input
                type={showAgentKey ? 'text' : 'password'}
                value={agentKey}
                onChange={(e) => {
                  setAgentKey(e.target.value);
                  setHasChanges(true);
                  setIsValidKey(null);
                }}
                placeholder="Írd be a Számlázz.hu agent key-t"
                className="w-full px-4 py-2 pr-24 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-2">
                {isValidKey !== null && (
                  <div>
                    {isValidKey ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircleIconOutline className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setShowAgentKey(!showAgentKey)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showAgentKey ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Az agent key-t a Számlázz.hu fiókodban találod meg a Beállítások → Számlázz.hu API menüpontban
            </p>
          </div>

          {isValidKey !== null && (
            <div className={`p-4 rounded-lg ${isValidKey ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start">
                {isValidKey ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-medium text-green-800">Agent key érvényes</h3>
                      <p className="text-sm text-green-700 mt-1">
                        A Számlázz.hu API kapcsolat sikeresen létrejött. Most már generálhatsz számlákat.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <XCircleIconOutline className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Agent key érvénytelen</h3>
                      <p className="text-sm text-red-700 mt-1">
                        Ellenőrizd az agent key-t és próbáld újra.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {billingSettings && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start">
                <BoltIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Számlázás aktív</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    A Számlázz.hu integráció be van állítva és használatra kész.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Bank Account Settings */}
          <div className="space-y-4 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Bankszámla adatok</h3>
            <p className="text-sm text-gray-500">
              Ezek az adatok megjelennek a számlákon. A számlázz.hu-ban is be kell állítani ezeket.
            </p>

            {/* HUF Bank Account */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
              <h4 className="text-sm font-medium text-gray-700">HUF számlázáshoz</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Számlavezető bank
                </label>
                <input
                  type="text"
                  value={bankHuf}
                  onChange={(e) => {
                    setBankHuf(e.target.value);
                    setHasChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="pl. OTP Bank Nyrt."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bankszámlaszám
                </label>
                <input
                  type="text"
                  value={bankAccountHuf}
                  onChange={(e) => {
                    setBankAccountHuf(e.target.value);
                    setHasChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="pl. 11742173-26044282"
                />
              </div>
            </div>

            {/* EUR Bank Account */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
              <h4 className="text-sm font-medium text-gray-700">EUR számlázáshoz</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Számlavezető bank
                </label>
                <input
                  type="text"
                  value={bankEur}
                  onChange={(e) => {
                    setBankEur(e.target.value);
                    setHasChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="pl. Slovenská sporiteľňa, a. s."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IBAN
                </label>
                <input
                  type="text"
                  value={ibanEur}
                  onChange={(e) => {
                    setIbanEur(e.target.value);
                    setHasChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="pl. SK23 0900 0000 0050 7704 3706"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SWIFT kód
                </label>
                <input
                  type="text"
                  value={swiftEur}
                  onChange={(e) => {
                    setSwiftEur(e.target.value);
                    setHasChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="pl. GIBASKBX"
                />
              </div>
            </div>
          </div>

          {/* Email Test Mode Toggle */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Email teszt mód</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Bekapcsolva minden email a teszt címre megy az ügyfelek helyett
                </p>
              </div>
              <div className="flex items-center gap-3">
                {billingSettings?.emailTestMode && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                    {billingSettings.emailTestAddress || 'nroland89@gmail.com'}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const newMode = !billingSettings?.emailTestMode;
                    setBillingSettings(prev => prev ? {
                      ...prev,
                      emailTestMode: newMode
                    } : null);
                    setHasChanges(true);
                    // Immediately sync to localStorage for instant effect
                    syncEmailTestModeFromOrg(newMode, billingSettings?.emailTestAddress);
                    toast.success(newMode ? 'Email teszt mód BEkapcsolva' : 'Email teszt mód KIkapcsolva');
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
                    billingSettings?.emailTestMode ? 'bg-amber-500' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      billingSettings?.emailTestMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleValidateAgentKey}
              disabled={isValidating || !agentKey.trim()}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidating ? 'Validálás...' : 'Validálás'}
            </button>
            <button
              type="button"
              onClick={handleSaveBillingSettings}
              disabled={!agentKey.trim() || !hasChanges}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <BoltIcon className="h-5 w-5" />
              <span>Mentés</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-md">
        <OrganizationUsers
          organization={{
            ...organization,
            users: organization.users || []
          }}
          onOrganizationUpdate={onOrganizationUpdate}
        />
      </div>
    </div>
  );
};

export default OrganizationSettings;
